// Per-grass-type agronomy targets and amendment-calculation helpers.
//
// Phase 1 scope (per the build plan): pH/lime-sulfur, K, P (overseeding-only),
// organic matter, CEC context, cautious Ca/Mg (dolomitic vs calcitic
// selection). Micronutrients are informational only — no recommendations
// emitted in Phase 1.
//
// Where present, targets and rates are sourced from university extension
// publications. Citations live in CITATIONS below and are referenced by id
// from each entry. A null nutrient target means this profile must omit that
// nutrient guidance until a reviewed source is available.

import type { Citation, GrassKey } from './types'

// ---------------------------------------------------------------------------
// Citations (shared references)
// ---------------------------------------------------------------------------

export const CITATIONS = {
  psu_soil_ph: {
    label: 'Effect of Soil pH on Nutrient Availability',
    url: 'https://extension.psu.edu/trees-lawns-and-landscaping/turfgrass-and-lawn-care/fertility-and-soil-management',
    publisher: 'Penn State Extension',
  },
  psu_turf_fertility: {
    label: 'Fertilizing Lawns',
    url: 'https://extension.psu.edu/trees-lawns-and-landscaping/turfgrass-and-lawn-care/fertility-and-soil-management',
    publisher: 'Penn State Extension',
  },
  umn_lawn_soil: {
    label: 'Lawn soil and fertilizer',
    url: 'https://extension.umn.edu/lawns-and-landscapes-minnesota/fertilizing-lawns',
    publisher: 'University of Minnesota Extension',
  },
  clemson_ph_change: {
    label: 'Changing the pH of Your Soil',
    url: 'https://hgic.clemson.edu/factsheet/changing-the-ph-of-your-soil/',
    publisher: 'Clemson HGIC',
  },
  clemson_centipede: {
    label: 'Centipedegrass Maintenance Calendar',
    url: 'https://hgic.clemson.edu/factsheet/centipedegrass-maintenance-calendar/',
    publisher: 'Clemson HGIC',
  },
  uf_st_augustine: {
    label: 'St. Augustinegrass for Florida Lawns',
    url: 'https://edis.ifas.ufl.edu/publication/LH010',
    publisher: 'UF/IFAS Extension',
  },
  uf_centipede: {
    label: 'Centipedegrass for Florida Lawns',
    url: 'https://edis.ifas.ufl.edu/publication/LH007',
    publisher: 'UF/IFAS Extension',
  },
  uf_bahia: {
    label: 'Bahiagrass for Florida Lawns',
    url: 'https://edis.ifas.ufl.edu/publication/LH006',
    publisher: 'UF/IFAS Extension',
  },
  tamu_bermuda: {
    label: 'Bermudagrass (AggieTurf)',
    url: 'https://aggieturf.tamu.edu/turfgrass-selection/bermudagrass/',
    publisher: 'Texas A&M AgriLife',
  },
  tamu_zoysia: {
    label: 'Zoysiagrass (AggieTurf)',
    url: 'https://aggieturf.tamu.edu/turfgrass-selection/zoysiagrass/',
    publisher: 'Texas A&M AgriLife',
  },
  uga_centipede: {
    label: 'Centipedegrass Decline',
    url: 'https://fieldreport.caes.uga.edu/publications/C1003/centipedegrass-decline/',
    publisher: 'UGA Extension',
  },
  csu_buffalo: {
    label: 'Buffalograss Lawns',
    url: 'https://extension.colostate.edu/resource/buffalograss-lawns/',
    publisher: 'Colorado State Extension',
  },
  uc_kikuyu: {
    label: 'Kikuyugrass (UC IPM Turf Species)',
    url: 'https://ipm.ucanr.edu/TOOLS/TURF/TURFSPECIES/kikuyu.html',
    publisher: 'UC IPM',
  },
  lsu_carpetgrass: {
    label: 'Carpetgrass for Lawns',
    url: 'https://www.lsuagcenter.com/topics/lawn_garden/home_gardening/lawn/turfgrass_varieties/carpetgrass-for-lawns',
    publisher: 'LSU AgCenter',
  },
  lsu_carpetgrass_care: {
    label: 'Carpetgrass lawn care',
    url: 'https://www.lsuagcenter.com/profiles/aiverson/articles/page1551975010823',
    publisher: 'LSU AgCenter',
  },
} as const satisfies Record<string, Citation>

export type CitationId = keyof typeof CITATIONS

export function cite(id: CitationId): Citation {
  return CITATIONS[id]
}

// ---------------------------------------------------------------------------
// Per-grass-type targets
// ---------------------------------------------------------------------------

export interface PhRange {
  /** Below this, lime is recommended. */
  min: number
  /** Target the engine aims for. */
  ideal: number
  /** Above this, sulfur is recommended. */
  max: number
}

export interface NutrientThreshold {
  /** ppm (Mehlich-3 or equivalent) below which a deficiency is flagged. */
  lowPpm: number
  /** ppm above which the level is considered sufficient; no action needed. */
  sufficientPpm: number
}

export interface GrassAgronomyTargets {
  displayName: string
  season: 'cool' | 'warm'
  ph: PhRange
  /** Null when no reviewed grass-specific potassium target is available. */
  potassium: NutrientThreshold | null
  /**
   * Most US states regulate phosphorus in lawn fertilizer; we only recommend
   * P at next overseeding. These thresholds inform when to flag rather than
   * when to recommend a free-form P application.
   */
  /** Null when no reviewed grass-specific phosphorus target is available. */
  phosphorus: NutrientThreshold | null
  /**
   * Centipedegrass and some warm-season grasses are sensitive to muriate of
   * potash (chloride). Sulfate of potash (0-0-50) is the safer default for
   * sensitive grasses.
   */
  /** Null when chloride sensitivity has not been established for this grass. */
  chlorideSensitive: boolean | null
  /** Citation ids that back this grass's targets. */
  citations: CitationId[]
}

export const GRASS_AGRONOMY: Record<GrassKey, GrassAgronomyTargets> = {
  'kentucky-bluegrass': {
    displayName: 'Kentucky Bluegrass',
    season: 'cool',
    ph: { min: 6.0, ideal: 6.5, max: 7.0 },
    potassium: { lowPpm: 100, sufficientPpm: 175 },
    phosphorus: { lowPpm: 25, sufficientPpm: 50 },
    chlorideSensitive: false,
    citations: ['psu_turf_fertility', 'umn_lawn_soil'],
  },
  'tall-fescue': {
    displayName: 'Tall Fescue',
    season: 'cool',
    ph: { min: 5.5, ideal: 6.5, max: 7.5 },
    potassium: { lowPpm: 100, sufficientPpm: 175 },
    phosphorus: { lowPpm: 25, sufficientPpm: 50 },
    chlorideSensitive: false,
    citations: ['psu_turf_fertility'],
  },
  'fine-fescue': {
    displayName: 'Fine Fescue',
    season: 'cool',
    ph: { min: 6.0, ideal: 6.5, max: 7.0 },
    potassium: { lowPpm: 100, sufficientPpm: 175 },
    phosphorus: { lowPpm: 25, sufficientPpm: 50 },
    chlorideSensitive: false,
    citations: ['psu_turf_fertility', 'umn_lawn_soil'],
  },
  'perennial-ryegrass': {
    displayName: 'Perennial Ryegrass',
    season: 'cool',
    ph: { min: 6.0, ideal: 6.5, max: 7.0 },
    potassium: { lowPpm: 100, sufficientPpm: 175 },
    phosphorus: { lowPpm: 25, sufficientPpm: 50 },
    chlorideSensitive: false,
    citations: ['psu_turf_fertility'],
  },
  'bermudagrass': {
    displayName: 'Bermudagrass',
    season: 'warm',
    ph: { min: 5.8, ideal: 6.5, max: 7.0 },
    // Bermuda benefits from elevated K for stress / wear tolerance.
    potassium: { lowPpm: 125, sufficientPpm: 200 },
    phosphorus: { lowPpm: 25, sufficientPpm: 50 },
    chlorideSensitive: false,
    citations: ['tamu_bermuda', 'clemson_ph_change'],
  },
  'zoysiagrass': {
    displayName: 'Zoysiagrass',
    season: 'warm',
    ph: { min: 6.0, ideal: 6.3, max: 6.8 },
    potassium: { lowPpm: 125, sufficientPpm: 200 },
    phosphorus: { lowPpm: 25, sufficientPpm: 50 },
    chlorideSensitive: false,
    citations: ['tamu_zoysia'],
  },
  'st-augustinegrass': {
    displayName: 'St. Augustinegrass',
    season: 'warm',
    ph: { min: 6.0, ideal: 6.5, max: 7.5 },
    potassium: { lowPpm: 100, sufficientPpm: 175 },
    phosphorus: { lowPpm: 25, sufficientPpm: 50 },
    chlorideSensitive: false,
    citations: ['uf_st_augustine'],
  },
  'centipede': {
    displayName: 'Centipedegrass',
    // Famously acid-loving. Above pH 6.0 it commonly shows iron chlorosis.
    season: 'warm',
    ph: { min: 4.5, ideal: 5.5, max: 6.0 },
    // Lower K target; centipede is a low-input grass and over-fertilization
    // (K included) is associated with "centipede decline."
    potassium: { lowPpm: 75, sufficientPpm: 150 },
    phosphorus: { lowPpm: 25, sufficientPpm: 50 },
    chlorideSensitive: true,
    citations: ['clemson_centipede', 'uf_centipede', 'uga_centipede'],
  },
  'bahiagrass': {
    displayName: 'Bahiagrass',
    season: 'warm',
    ph: { min: 5.5, ideal: 6.0, max: 6.5 },
    potassium: { lowPpm: 100, sufficientPpm: 175 },
    phosphorus: { lowPpm: 25, sufficientPpm: 50 },
    chlorideSensitive: false,
    citations: ['uf_bahia'],
  },
  'carpetgrass': {
    displayName: 'Carpetgrass',
    season: 'warm',
    // LSU gives a broad pH range of 4-7. Use its midpoint only for the
    // calculator's amendment estimate.
    ph: { min: 4.0, ideal: 5.5, max: 7.0 },
    // LSU sources reviewed for this profile do not establish P/K soil-test
    // thresholds or chloride sensitivity. Do not inherit generic turf values.
    potassium: null,
    phosphorus: null,
    chlorideSensitive: null,
    citations: ['lsu_carpetgrass', 'lsu_carpetgrass_care'],
  },
  'buffalograss': {
    displayName: 'Buffalograss',
    // Buffalograss tolerates and prefers alkaline soils common in its native
    // Great Plains range.
    season: 'warm',
    ph: { min: 6.5, ideal: 7.0, max: 8.0 },
    potassium: { lowPpm: 100, sufficientPpm: 175 },
    phosphorus: { lowPpm: 25, sufficientPpm: 50 },
    chlorideSensitive: false,
    citations: ['csu_buffalo'],
  },
  'kikuyugrass': {
    displayName: 'Kikuyugrass',
    // Tolerates a wide pH band in coastal soils; a lighter feeder than
    // bermuda, so standard K thresholds apply rather than elevated ones.
    season: 'warm',
    // Max matches the guide's stated 5.5-7.5 band so the interpreter doesn't
    // recommend sulfur inside a range the guide calls acceptable.
    ph: { min: 5.5, ideal: 6.5, max: 7.5 },
    potassium: { lowPpm: 100, sufficientPpm: 175 },
    phosphorus: { lowPpm: 25, sufficientPpm: 50 },
    chlorideSensitive: false,
    citations: ['uc_kikuyu', 'clemson_ph_change'],
  },
  'annual-ryegrass': {
    displayName: 'Annual Ryegrass',
    // Winter-overseed annual; low-input, one-season turf with modest
    // fertility needs.
    season: 'cool',
    ph: { min: 6.0, ideal: 6.5, max: 7.0 },
    potassium: { lowPpm: 100, sufficientPpm: 175 },
    phosphorus: { lowPpm: 25, sufficientPpm: 50 },
    chlorideSensitive: false,
    citations: ['psu_turf_fertility'],
  },
  'mixed-cool-season-blend': {
    displayName: 'Mixed Cool-Season Blend',
    // Consensus targets across the blend's components (bluegrass, ryegrass,
    // fescue), which all share the same cool-season fertility profile.
    season: 'cool',
    ph: { min: 6.0, ideal: 6.5, max: 7.0 },
    potassium: { lowPpm: 100, sufficientPpm: 175 },
    phosphorus: { lowPpm: 25, sufficientPpm: 50 },
    chlorideSensitive: false,
    citations: ['psu_turf_fertility', 'umn_lawn_soil'],
  },
}

// ---------------------------------------------------------------------------
// Amendment calculations
// ---------------------------------------------------------------------------

/** Maximum lime application in a single pass. Splits above this are required. */
export const LIME_SINGLE_APPLICATION_CAP_LBS_PER_1000 = 50

/**
 * Maximum elemental-sulfur application in a single pass on established turf.
 * Sulfur burns turf at far lower rates than lime, so extension turf guidance
 * (Clemson HGIC, Penn State) keeps each pass to about 5 lb per 1,000 sq ft,
 * spaced weeks apart and away from summer heat.
 */
export const SULFUR_SINGLE_APPLICATION_CAP_LBS_PER_1000 = 5

/** Threshold above which a soil is considered to have low organic matter. */
export const ORGANIC_MATTER_LOW_THRESHOLD_PCT = 3.0

/** Below this, recommend compost amendment (more aggressive than topdressing). */
export const ORGANIC_MATTER_VERY_LOW_THRESHOLD_PCT = 2.0

export type SoilTextureClass = 'sandy' | 'loam' | 'clay'

/**
 * Bucket CEC into a coarse soil-texture proxy. This is a heuristic — actual
 * texture requires a separate test — but CEC is a defensible proxy for the
 * lime-calc purposes here.
 *
 * Sources: Penn State (https://extension.psu.edu/trees-lawns-and-landscaping/turfgrass-and-lawn-care/fertility-and-soil-management)
 *          UMN     (https://extension.umn.edu/lawns-and-landscapes-minnesota/fertilizing-lawns)
 */
export function classifyCec(cec: number): { class: SoilTextureClass; label: string } {
  if (cec < 7) return { class: 'sandy', label: 'low (sandy soils typically <7)' }
  if (cec < 15) return { class: 'loam', label: 'moderate (loamy soils typically 7-15)' }
  return { class: 'clay', label: 'high (clay or organic soils typically >15)' }
}

/** One pass of an even split, to a tenth of a pound per 1,000 sq ft. */
function evenPassLbs(totalLbsPer1000: number, applications: number): number {
  return Math.round((totalLbsPer1000 / Math.max(1, applications)) * 10) / 10
}

export interface LimeRecommendation {
  /** lb of CaCO3-equivalent per 1000 sq ft total to reach target pH. */
  totalLbsPer1000: number
  /** Even per-pass rate: the total split across the applications, never above the cap. */
  singleApplicationLbsPer1000: number
  /** True if the total exceeds the cap and must be split across multiple passes. */
  splitRequired: boolean
  /** How many applications the engine recommends, given the cap. */
  applicationsRecommended: number
  /** Whether dolomitic (with Mg) or calcitic (Ca-only) is preferred. */
  preferredSource: 'dolomitic' | 'calcitic'
  preferredSourceReason: string
  /** Citation ids backing this calculation. */
  citations: CitationId[]
}

/**
 * Estimate lime needed to raise soil pH to a target. Uses a conservative
 * lb-per-pH-unit lookup by CEC class. Real-world need varies; recommend
 * re-test after each application.
 *
 * @param currentPh measured pH
 * @param targetPh ideal pH per grass type
 * @param cec measured CEC in meq/100g (used to bucket texture)
 * @param magnesiumPpm if low (<60ppm), prefer dolomitic lime to supply Mg
 */
export function calculateLimeRecommendation(
  currentPh: number,
  targetPh: number,
  cec: number,
  magnesiumPpm: number | null,
): LimeRecommendation | null {
  if (currentPh >= targetPh) return null

  const phDeficit = targetPh - currentPh
  const texture = classifyCec(cec)

  // lb CaCO3-equivalent per 1000 sq ft to raise pH by 0.5 unit.
  // Conservative end of published university extension ranges.
  const lbsPer1000PerHalfUnit: Record<SoilTextureClass, number> = {
    sandy: 25,
    loam: 40,
    clay: 60,
  }

  const totalLbsPer1000 = Math.round(
    (phDeficit / 0.5) * lbsPer1000PerHalfUnit[texture.class],
  )

  const splitRequired = totalLbsPer1000 > LIME_SINGLE_APPLICATION_CAP_LBS_PER_1000
  const applicationsRecommended = Math.ceil(
    totalLbsPer1000 / LIME_SINGLE_APPLICATION_CAP_LBS_PER_1000,
  )
  const singleApplicationLbsPer1000 = evenPassLbs(totalLbsPer1000, applicationsRecommended)

  // Default to calcitic unless magnesium is clearly low.
  const mgLow = magnesiumPpm !== null && magnesiumPpm < 60
  const preferredSource: 'dolomitic' | 'calcitic' = mgLow ? 'dolomitic' : 'calcitic'
  const preferredSourceReason = mgLow
    ? `Magnesium is low (${magnesiumPpm} ppm). Dolomitic lime supplies both calcium and magnesium.`
    : 'Magnesium is adequate. Calcitic lime is preferred to avoid over-applying magnesium.'

  return {
    totalLbsPer1000,
    singleApplicationLbsPer1000,
    splitRequired,
    applicationsRecommended,
    preferredSource,
    preferredSourceReason,
    citations: ['psu_soil_ph', 'clemson_ph_change'],
  }
}

export interface SulfurRecommendation {
  totalLbsPer1000: number
  /** Even per-pass rate: the total split across the applications, never above the cap. */
  singleApplicationLbsPer1000: number
  splitRequired: boolean
  applicationsRecommended: number
  citations: CitationId[]
}

/**
 * Estimate elemental sulfur needed to lower soil pH to a target. More
 * conservative than lime because sulfur changes pH faster and over-application
 * can drop pH abruptly.
 */
export function calculateSulfurRecommendation(
  currentPh: number,
  targetPh: number,
  cec: number,
): SulfurRecommendation | null {
  if (currentPh <= targetPh) return null

  const phExcess = currentPh - targetPh
  const texture = classifyCec(cec)

  // lb elemental sulfur per 1000 sq ft to lower pH by 0.5 unit.
  // Conservative — recommend re-test after first application.
  const lbsPer1000PerHalfUnit: Record<SoilTextureClass, number> = {
    sandy: 5,
    loam: 8,
    clay: 12,
  }

  const totalLbsPer1000 = Math.round(
    (phExcess / 0.5) * lbsPer1000PerHalfUnit[texture.class],
  )

  const splitRequired = totalLbsPer1000 > SULFUR_SINGLE_APPLICATION_CAP_LBS_PER_1000
  const applicationsRecommended = Math.ceil(
    totalLbsPer1000 / SULFUR_SINGLE_APPLICATION_CAP_LBS_PER_1000,
  )
  const singleApplicationLbsPer1000 = evenPassLbs(totalLbsPer1000, applicationsRecommended)

  return {
    totalLbsPer1000,
    singleApplicationLbsPer1000,
    splitRequired,
    applicationsRecommended,
    citations: ['psu_soil_ph', 'clemson_ph_change'],
  }
}

export type PotassiumSource = 'sulfate_of_potash' | 'muriate_of_potash'

export interface PotassiumRecommendation {
  /** Per-1000-sqft rate of actual K2O to apply. */
  k2oLbsPer1000: number
  source: PotassiumSource
  sourceReason: string
  /** Approximate product weight, assuming sulfate-of-potash (0-0-50) or muriate (0-0-60). */
  productLbsPer1000: number
  citations: CitationId[]
}

/**
 * Recommend a K application rate. Phase 1 rule of thumb: bring under-target
 * soils up by applying 1 lb K2O per 1000 sqft per growing season per nutrient
 * gap "tier" (low vs very low). Source picks sulfate when the grass is
 * chloride-sensitive (centipede).
 */
export function calculatePotassiumRecommendation(
  potassiumPpm: number,
  target: NutrientThreshold,
  chlorideSensitive: boolean,
): PotassiumRecommendation | null {
  if (potassiumPpm >= target.sufficientPpm) return null

  // Tiered rate: deeper deficiency gets a heavier rate, capped at 2 lb K2O / M.
  let k2oLbsPer1000: number
  if (potassiumPpm < target.lowPpm * 0.5) {
    k2oLbsPer1000 = 2.0
  } else if (potassiumPpm < target.lowPpm) {
    k2oLbsPer1000 = 1.5
  } else {
    k2oLbsPer1000 = 1.0
  }

  const source: PotassiumSource = chlorideSensitive
    ? 'sulfate_of_potash'
    : 'sulfate_of_potash' // Default sulfate even for non-sensitive; it's gentler. Muriate is acceptable but not preferred.
  const sourceReason = chlorideSensitive
    ? 'This grass is sensitive to chloride. Use sulfate of potash (0-0-50), not muriate (0-0-60).'
    : 'Sulfate of potash (0-0-50) is the gentler default. Muriate (0-0-60) is acceptable for non-sensitive grasses.'

  // Product weight assuming K2O % of source
  const k2oPct = source === 'sulfate_of_potash' ? 0.5 : 0.6
  const productLbsPer1000 = Math.round((k2oLbsPer1000 / k2oPct) * 10) / 10

  return {
    k2oLbsPer1000,
    source,
    sourceReason,
    productLbsPer1000,
    citations: ['psu_turf_fertility'],
  }
}

/**
 * Lawn-size math: convert a per-1000-sqft rate into total product weight for
 * the user's lawn, rounded to a sensible bag-buying number.
 */
export function totalProductLbsForLawn(lbsPer1000: number, lawnSizeSqft: number | null): number | null {
  if (!lawnSizeSqft || lawnSizeSqft <= 0) return null
  const raw = (lbsPer1000 * lawnSizeSqft) / 1000
  return Math.round(raw * 10) / 10
}
